import { Alert, Card, Flex, SimpleTable, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const AlertPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Alert</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Alert component in Djuno Design is used to display important
            messages to users with various types of visual emphasis. It supports
            different alert types, icons, and banner modes, making it a
            versatile tool for delivering notifications or feedback in your
            application.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Alert component comes with several customizable options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Type
                </Text>
                : Choose from neutral, success, info, warning, and error to
                match the context.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Icon Display
                </Text>
                : Optionally display an icon that corresponds to the type of
                alert.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Banner Mode
                </Text>
                : The alert can be rendered as a banner, which adjusts its
                visual style to remove borders and rounding for a full-width
                banner look.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4">
            <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
              <ExpandableCard
                codeViewerProps={{
                  code: AlertExample1,
                }}
              >
                <Flex direction={"col"} className="gap-3 w-full">
                  <Text strong size="sm">
                    basic usage
                  </Text>
                  <Alert message="Djuno Design. Neutral Alert" />
                  <Alert uiType="info" message="Djuno Design. Info Alert" />
                  <Alert
                    uiType="warning"
                    message="Djuno Design. Warning Alert"
                  />
                  <Alert
                    uiType="success"
                    message="Djuno Design. Success Alert"
                  />
                  <Alert uiType="error" message="Djuno Design. Error Alert" />
                  <Text strong size="sm" className="mt-4">
                    with description
                  </Text>
                  <Alert
                    message={"Djuno Design. Alert with Description"}
                    description="Error Description Error Description Error Description Error Description Error Description Error Description"
                    uiType="info"
                  />
                  <Text strong size="sm" className="mt-4">
                    with icon
                  </Text>
                  <Alert showIcon message="Djuno Design. Neutral Alert" />
                  <Alert
                    showIcon
                    uiType="info"
                    message="Djuno Design. Info Alert"
                  />
                  <Alert
                    showIcon
                    uiType="warning"
                    message="Djuno Design. Warning Alert"
                  />
                  <Alert
                    showIcon
                    uiType="success"
                    message="Djuno Design. Success Alert"
                  />
                  <Alert
                    showIcon
                    uiType="error"
                    message="Djuno Design. Error Alert"
                  />
                  <Alert
                    showIcon
                    message="Djuno Design. Alert with Icon"
                    description="Error Description Error Description Error Description Error Description Error Description Error Description"
                    uiType="info"
                  />
                  <Text strong size="sm" className="mt-4">
                    banner mode
                  </Text>
                  <Alert
                    banner
                    showIcon
                    uiType="info"
                    message="Djuno Design. Info Alert"
                  />
                </Flex>
              </ExpandableCard>
            </Flex>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* message */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">message</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The main content or message to be displayed in the
                        alert. Can be a simple string or a React node.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* description */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">description</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Optional description text that appears below the main
                        message.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* type */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Specifies the type of the alert to change its visual
                        style
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Alert.ts#L36"}
                        size="xs"
                        code
                      >
                        AlertTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      {" "}
                      <Text size="sm">neutral</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* showIcon */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">showIcon</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Determines whether an icon should be displayed with the
                        alert
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* banner */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">banner</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        If true, renders the alert as a banner with a full-width
                        appearance and no border rounding
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>false</SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">The content inside the alert.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* closable */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">closable</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        If true, displays a close button for dismissing the
                        alert.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* onClose */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onClose</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Callback function triggered when the alert is closed, if
                        `closable` is true.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`() => void`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const AlertExample1 = `<Flex direction={"col"} className="gap-3 w-full">
    <Text strong size="sm">
      basic usage
    </Text>
    <Alert message="Djuno Design. Neutral Alert" />
    <Alert type="info" message="Djuno Design. Info Alert" />
    <Alert type="warning" message="Djuno Design. Warning Alert" />
    <Alert type="success" message="Djuno Design. Success Alert" />
    <Alert type="error" message="Djuno Design. Error Alert" />
    <Text strong size="sm" className="mt-4">
      with description
    </Text>
    <Alert
      message={"Djuno Design. Alert with Description"}
      description="Error Description Error Description Error Description Error Description Error Description Error Description"
      type="info"
    />
    <Text strong size="sm" className="mt-4">
      with icon
    </Text>
    <Alert showIcon message="Djuno Design. Neutral Alert" />
    <Alert
      showIcon
      type="info"
      message="Djuno Design. Info Alert"
    />
    <Alert
      showIcon
      type="warning"
      message="Djuno Design. Warning Alert"
    />
    <Alert
      showIcon
      type="success"
      message="Djuno Design. Success Alert"
    />
    <Alert
      showIcon
      type="error"
      message="Djuno Design. Error Alert"
    />
    <Alert
      showIcon
      message="Djuno Design. Alert with Icon"
      description="Error Description Error Description Error Description Error Description Error Description Error Description"
      type="info"
    />
    <Text strong size="sm" className="mt-4">
      banner mode
    </Text>
    <Alert
      banner
      showIcon
      type="info"
      message="Djuno Design. Info Alert"
    />
  </Flex>`;

export default AlertPage;
